import {
  FileText,
  type LucideIcon,
  Package,
  Palette,
  Tag,
  Target,
  Truck,
} from "lucide-react";

import type { ToolStep } from "@intelligems/ig-types/schemas/ai/tool-progress";
import { type ProgressStep, StepRows } from "@src/components/steps/StepRows";

/**
 * The steps a single tool call reported while it ran.
 *
 * A tool that reads several things at once is one call to the model and
 * several steps to the person waiting. This is the second, drawn inside the
 * card that already represents the call rather than in a second place.
 *
 * State is carried twice on purpose. A running step glares *and* is worded
 * in the present tense — "Reviewing your price history" becomes "Reviewed your
 * price history" — so the row still reads correctly in a screenshot, to a
 * reader who cannot see the animation, and under `prefers-reduced-motion`.
 * The tool owns both wordings and sends whichever applies with each report.
 */
export const ToolSteps = ({
  steps,
  compact,
  className,
}: {
  steps: ToolStep[];
  compact?: boolean;
  /** Merged last, so a surface can change the block's inset. */
  className?: string;
}) => (
  <StepRows
    compact={compact}
    className={className}
    steps={steps.map((step) => ({
      id: step.id,
      label: step.label ?? step.id,
      status: STATUS[step.status],
      detail: step.reason,
      icon: step.icon ? NAMED_ICONS[step.icon] : undefined,
    }))}
  />
);

const STATUS: Record<ToolStep["status"], ProgressStep["status"]> = {
  pending: "pending",
  running: "running",
  ok: "done",
  unavailable: "skipped",
  failed: "failed",
};

/**
 * Glyph names a tool may use, and what they draw.
 *
 * The wire carries a name, not a component, so this is the map. Kept small and
 * additive on purpose: a tool naming something absent here still renders,
 * falling back to the status glyph rather than an empty row.
 */
const NAMED_ICONS: Record<string, LucideIcon> = {
  brand: Palette,
  catalog: Package,
  document: FileText,
  offer: Tag,
  pricing: Target,
  shipping: Truck,
};
